class RecurringCallback<T extends any[] = []> {
    private state: boolean = false;
    private interval: number = 0;
    private intervalHandle: NodeJS.Timeout | null = null;
    private intervalCallback: (...args: T) => void = () => {};
    private args: T = [] as unknown as T;
    private cycles: number = 0;

    /**
     * Starts a recurring callback
     * @param callback The callback function to run
     * @param interval The interval time in ms
     * @param args Optional arguments to pass to the callback
     */
    start(callback: (...args: T) => void, interval: number, ...args: T): void {
        if (this.state) return;

        this.intervalCallback = callback;
        this.interval = interval;
        this.args = args;

        this.createInterval();
        this.state = true;
    }

    /**
     * Stops a recurring callback
     */    
    stop(): void {
        if (!this.state) return;
        this.clearInterval();
        this.state = false;
    }

    /**
     * Reschedules the existing callback. The elapsed time since the last callback is reset to 0.
     */ 
    reschedule(): void {
        if (!this.state) return;
        this.clearInterval();
        this.createInterval();
    }

    /**
     * Immediately runs the callback function.
     * @param reschedule Whether or not the time for the next scheduled callback should be reset to 0
     */    
    immediate(reschedule: boolean): void {
        this.intervalCallback(...this.args);
        if (reschedule) this.reschedule();
    }

    /**
     * Returns whether the interval is running or not
     * @returns True if the interval is running
     */    
    isActive(): boolean {
        return this.state;
    }

    /**
     * Returns the number of elapsed cycles
     * @returns The number of elapsed cycles
     */      
    getCycles(): number {
        return this.cycles;
    }

    private createInterval(): void {
        this.cycles = 0;
        this.intervalHandle = setInterval(() => {
            this.cycles ++;
            this.intervalCallback(...this.args);
        }, this.interval);
    }

    private clearInterval(): void {
        if (this.intervalHandle) {
            clearInterval(this.intervalHandle);
            this.intervalHandle = null;
            this.cycles = 0;
        }
    }
}

export default RecurringCallback;
